import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';

//  User components
import './styles/style.css';
import ThemeProvider from './theme';
import Dashboard from './components/layout/Dashboard';
import Corpus from './components/corpus/Corpus';
import Corpuses from './components/corpus/Corpuses';
import Course from './components/course/Course';
import Courses from './components/course/Courses';
import Document from './components/doc/Document';
import Documents from './components/doc/Documents';
import Register from './components/auth/Register';
import Login from './components/auth/Login';
import Profile from './components/user/Profile';
import Chat from './components/chat/Chat';
import NewChat from './components/chat/NewChat';
import Users from './components/admin/Users';
import Home from './components/home/Home';
import PageNotFound from './components/util/PageNotFound';
import { mislecAPI } from './util/axios';
import MaterialProvider from './context/MaterialContext';

function App() {
  const [user, setUser] = React.useState(null);
  const [chats, setChats] = React.useState([]);
  const [courses, setCourses] = React.useState([]);
  const [fethced, setFetched] = React.useState(false);
  const [expired, setExpired] = React.useState(false);

  React.useEffect(() => {
    mislecAPI.interceptors.request.use((request) => {
      if (request.headers)
        request.headers['Authorization'] = 'Bearer ' + sessionStorage.getItem('mISLec_access_token') || ''

      return request
    },
      (error) => { return Promise.reject(error) }
    )
  }, [])

  const updateUser = (user) => {
    if (!user) {
      setFetched(false);
      setChats([]);
    }
    setUser(user);
  }

  React.useEffect(() => {
    //  Access token
    const accessToken = sessionStorage.getItem('mISLec_access_token');

    if (user) return;
    if (!accessToken) return;

    //  Decode token
    const decodedToken = JSON.parse(atob(accessToken.split('.')[1]));

    //  Fetch user data
    mislecAPI.get(`/api/users/${decodedToken.user._id}`)
      .then(res => {
        updateUser(res.data);
      })
      .catch(err => {
        //  Notify user of expired token
        setExpired(true);
        setUser(null);
      });
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    if (fethced) return;
    fetchChats();
    fetchCourses()
    setFetched(true);
  }, [user, chats]);

  const fetchChats = () => {
    if (!user) return;

    //  Fetch documents from the server
    mislecAPI.get(`/api/chat/history/${user._id}`)
      .then(res => {
        setChats(res.data.chats);
      })
      .catch(error => {
        if (error.response.data.detail === 'api.auth.verify.401') {
          //  Expired session
          updateUser(null);
        }
      });
  }

  const fetchCourses = () => {
    if (!user) return;

    mislecAPI.get(`/api/courses`)
      .then(res => {
        setCourses(res.data.courses)
      })
      .catch(error => {
        if (error.response.data.detail === 'api.auth.verify.401') {
          //  Expired session
          updateUser(null);
        }
      });
  }

  const router = createBrowserRouter(
    [
      {
        element: (
          <Dashboard user={user} updateUser={updateUser} chats={chats} fetchChats={fetchChats} courses={courses}>
            <Suspense>
              <Outlet />
            </Suspense>
          </Dashboard>
        ),
        children: [
          {
            path: '/chat',
            element: <NewChat user={user} updateUser={updateUser} fetchChats={fetchChats} />
          },
          {
            path: '/chat/:id',
            element: <Chat user={user} updateUser={updateUser} fetchChats={fetchChats} />
          },
          {
            path: '/profile',
            element: <Profile user={user} updateUser={updateUser} />
          },
          {
            path: '/documents/:id',
            element: <Document user={user} updateUser={updateUser} />
          },
          {
            path: '/documents',
            element: <Documents user={user} updateUser={updateUser} />
          },
          {
            path: '/corpus',
            element: <Corpuses user={user} updateUser={updateUser} />
          },
          {
            path: '/corpus/:id',
            element: <Corpus user={user} updateUser={updateUser} />
          },
          {
            path: '/course',
            element: <Courses user={user} updateUser={updateUser} />
          },
          {
            path: '/course/:id/:tab?/:section?',
            element: <MaterialProvider><Course user={user} updateUser={updateUser} /></MaterialProvider>
          },
          {
            path: '/users',
            element: <Users user={user} updateUser={updateUser} />
          }
        ]
      },
      {
        path: '/register',
        element: <Register />
      },
      {
        path: '/login',
        element: <Login onLogin={updateUser} expired={expired} setExpired={setExpired} />
      },
      {
        path: '/',
        element: <Home user={user} updateUser={updateUser} />
      },
      {
        path: '/404',
        element: <PageNotFound user={user} updateUser={updateUser} />
      },
      {
        path: '*',
        element: <Navigate to='/404' replace />,
      }
    ]
  );

  return (
    <>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </>
  );
}

export default App;
